import { useLocaleTag } from "@/lib/i18n/format";
import type { Translate } from "@/lib/i18n/translate";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { Plus, RotateCcw, X } from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";
import type {
  CalculatorState,
  DerivedCustom,
  DerivedTool,
} from "./pricing-calculator";
import {
  DarkPopover,
  InfoButton,
  type InfoId,
  TitleWithInfo,
  popoverBody,
} from "./pricing-popovers";
import { T } from "./pricing-shared";
import { SupportFeeRow } from "./pricing-support-fee-row";

/** Cost blocks we do not touch. `useTranslations("public.pricingCompareCards")`. */
function rightUnchangedLabels(t: Translate): readonly string[] {
  return [
    t("buchhaltungAmpErp"),
    t("ePostVersandBriefmarken"),
    t("festnetzAmpMobiltarife"),
    t("kiNutzung"),
  ];
}

const TOOL_LABEL: CSSProperties = {
  fontFamily: T.sans,
  fontSize: 14.5,
  fontWeight: 600,
  lineHeight: 1.35,
  color: T.ink,
};

function ToolRow({
  tool,
  calc,
  openInfo,
  onToggle,
  onClose,
}: {
  readonly tool: DerivedTool;
  readonly calc: CalculatorState;
  readonly openInfo: InfoId | null;
  readonly onToggle: (id: InfoId) => void;
  readonly onClose: () => void;
}): ReactNode {
  const localeTag = useLocaleTag();
  const t = useTranslations("public.pricingCompareCards");
  const { def } = tool;
  const infoId = def.info ?? null;
  const valueColor = tool.overridden ? T.accent : T.ink;
  return (
    <div
      style={{
        position: "relative",
        padding: "13px 0",
        borderTop: "1px solid rgba(11,15,25,0.07)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          {infoId ? (
            <TitleWithInfo
              title={def.label}
              style={TOOL_LABEL}
              info={
                <InfoButton
                  open={openInfo === infoId}
                  onToggle={() => onToggle(infoId)}
                  label={t("mehrInfosZu", { title: def.label })}
                />
              }
            />
          ) : (
            <span style={TOOL_LABEL}>{def.label}</span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            flex: "none",
          }}
        >
          {tool.overridden ? (
            <button
              type="button"
              onClick={() => calc.resetOverride(def.key)}
              aria-label={t("zuruecksetzen")}
              title={t("wiederAutomatischBerechnen")}
              className="mkpreise-iconbtn"
              style={{
                appearance: "none",
                cursor: "pointer",
                border: "none",
                background: "none",
                padding: "2px 3px",
                lineHeight: 0,
                color: "#9AA0A6",
                display: "inline-flex",
              }}
            >
              <RotateCcw size={14} strokeWidth={1.9} aria-hidden="true" />
            </button>
          ) : null}
          <span
            style={{
              fontFamily: T.sans,
              fontSize: 15,
              fontWeight: 700,
              color: valueColor,
            }}
          >
            {tool.value.toLocaleString(localeTag)} €
          </span>
        </div>
      </div>
      {def.sub ? (
        <div
          style={{
            fontFamily: T.sans,
            fontSize: 11.5,
            color: "#8A8D94",
            marginTop: 2,
          }}
        >
          {def.sub}
        </div>
      ) : null}
      <input
        type="range"
        min={0}
        max={tool.sliderMax}
        step={def.step}
        value={tool.value}
        onChange={(e) => calc.setOverride(def.key, Number(e.target.value))}
        className="mkpreise-tool-range"
        style={{
          width: "100%",
          height: 5,
          borderRadius: 999,
          cursor: "pointer",
          margin: "11px 0 1px",
          background: tool.trackBg,
        }}
      />
      {infoId && openInfo === infoId ? (
        <DarkPopover
          onClose={onClose}
          width="min(322px, 100%)"
          position={{ top: 60, left: 0 }}
          padding="20px 22px"
        >
          {popoverBody(infoId)}
        </DarkPopover>
      ) : null}
    </div>
  );
}

function CustomRow({
  custom,
  calc,
}: {
  readonly custom: DerivedCustom;
  readonly calc: CalculatorState;
}): ReactNode {
  const localeTag = useLocaleTag();
  const t = useTranslations("public.pricingCompareCards");
  const { row } = custom;
  return (
    <div
      style={{
        position: "relative",
        padding: "13px 0",
        borderTop: "1px solid rgba(11,15,25,0.07)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <input
          type="text"
          value={row.name}
          onChange={(e) => calc.renameCustom(row.id, e.target.value)}
          placeholder={t("eigeneSoftware")}
          className="mkpreise-custom-input"
          style={{
            flex: 1,
            minWidth: 0,
            padding: "4px 9px",
            border: "1px solid rgba(11,15,25,0.14)",
            borderRadius: 8,
            background: "#fff",
            fontFamily: T.sans,
            fontSize: 14,
            fontWeight: 600,
            color: T.ink,
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: "none",
          }}
        >
          <span
            style={{
              fontFamily: T.sans,
              fontSize: 15,
              fontWeight: 700,
              color: row.value > 0 ? T.accent : T.ink,
            }}
          >
            {row.value.toLocaleString(localeTag)} €
          </span>
          <button
            type="button"
            onClick={() => calc.removeCustom(row.id)}
            aria-label={t("entfernen")}
            title={t("entfernen")}
            style={{
              appearance: "none",
              cursor: "pointer",
              border: "none",
              background: "none",
              padding: "2px 5px",
              lineHeight: 0,
              color: "#BBB6AC",
              display: "inline-flex",
            }}
          >
            <X size={17} strokeWidth={1.9} aria-hidden="true" />
          </button>
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={custom.sliderMax}
        step={5}
        value={row.value}
        onChange={(e) => calc.setCustomValue(row.id, Number(e.target.value))}
        className="mkpreise-tool-range"
        style={{
          width: "100%",
          height: 5,
          borderRadius: 999,
          cursor: "pointer",
          margin: "11px 0 1px",
          background: custom.trackBg,
        }}
      />
    </div>
  );
}

/** Body of the "Ohne hausverwalter.ai" card (rows + custom + locked row). */
export function LeftCardBody({
  calc,
  openInfo,
  onToggle,
  onClose,
}: {
  readonly calc: CalculatorState;
  readonly openInfo: InfoId | null;
  readonly onToggle: (id: InfoId) => void;
  readonly onClose: () => void;
}): ReactNode {
  const t = useTranslations("public.pricingCompareCards");
  return (
    <>
      {calc.tools.map((tool) => (
        <ToolRow
          key={tool.def.key}
          tool={tool}
          calc={calc}
          openInfo={openInfo}
          onToggle={onToggle}
          onClose={onClose}
        />
      ))}
      {calc.customs.map((custom) => (
        <CustomRow key={custom.row.id} custom={custom} calc={calc} />
      ))}
      <button
        type="button"
        onClick={calc.addCustom}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          width: "100%",
          marginTop: 2,
          padding: "14px 0 4px",
          border: "none",
          borderTop: "1px solid rgba(11,15,25,0.07)",
          background: "none",
          cursor: "pointer",
          fontFamily: T.sans,
          fontSize: 13.5,
          fontWeight: 600,
          color: T.ink,
          textAlign: "left",
        }}
      >
        <span
          style={{
            width: 19,
            height: 19,
            borderRadius: "50%",
            border: `1.5px solid ${T.ink}`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          <Plus size={12} strokeWidth={2.2} aria-hidden="true" />
        </span>
        {t("eigeneSoftwareHinzufuegen")}
      </button>
      <div
        style={{
          position: "relative",
          padding: "13px 0",
          borderTop: "1px solid rgba(11,15,25,0.07)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span
            style={{
              fontFamily: T.sans,
              fontSize: 14.5,
              fontWeight: 600,
              color: "#9AA0A6",
            }}
          >
            {t("buchhaltungAmpErp")}
          </span>
          <span
            style={{
              fontFamily: T.label,
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#BBB6AC",
              border: "1px solid rgba(11,15,25,0.10)",
              borderRadius: 999,
              padding: "4px 11px",
              flex: "none",
            }}
          >
            {t("bleibt")}
          </span>
        </div>
        <div
          style={{
            fontFamily: T.sans,
            fontSize: 11.5,
            color: "#8A8D94",
            marginTop: 2,
          }}
        >
          {t("bleibtErsetzenNicht")}
        </div>
      </div>
    </>
  );
}

function GroupDivider({
  label,
  accent,
}: {
  readonly label: string;
  readonly accent: boolean;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        margin: "20px 0 9px",
      }}
    >
      <span
        style={{
          fontFamily: T.label,
          fontSize: 10,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: accent ? T.accent : "#9AA0A6",
          flex: "none",
        }}
      >
        {label}
      </span>
      <span
        style={{
          flex: 1,
          height: 1,
          background: accent ? "rgba(0,128,112,0.2)" : "rgba(11,15,25,0.1)",
        }}
      />
    </div>
  );
}

/** Body of the "Mit hausverwalter.ai" card (3 groups). */
export function RightCardBody({
  calc,
  compact,
}: {
  readonly calc: CalculatorState;
  readonly compact: boolean;
}): ReactNode {
  const t = useTranslations("public.pricingCompareCards");
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "13px 14px",
          borderRadius: 12,
          background: "rgba(0,128,112,0.07)",
          border: "1px solid rgba(0,128,112,0.18)",
        }}
      >
        <BrandLogoMark width={34} height={34} variant="dark" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: T.sans,
              fontSize: 14.5,
              fontWeight: 700,
              color: T.ink,
            }}
          >
            {t("hausverwalterAi2")}
          </div>
        </div>
        <div style={{ textAlign: "right", flex: "none" }}>
          <div
            style={{
              fontFamily: T.sans,
              fontSize: 15.5,
              fontWeight: 700,
              color: T.ink,
            }}
          >
            {calc.hvCostLabel} €
          </div>
          <div
            style={{
              fontFamily: T.label,
              fontSize: 9.5,
              color: "#9AA0A6",
              marginTop: 2,
            }}
          >
            {calc.unitsLabel} {t("k49Ct")}
          </div>
        </div>
      </div>

      <SupportFeeRow calc={calc} />

      <GroupDivider
        label={compact ? t("guenstiger") : t("machenGuenstiger")}
        accent
      />
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "0 2px",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: T.sans,
              fontSize: 14.5,
              fontWeight: 600,
              color: T.ink,
            }}
          >
            {t("guenstigerEMailAnbieter")}
          </div>
          <div
            style={{
              fontFamily: T.sans,
              fontSize: 12,
              lineHeight: 1.45,
              color: "#8A8D94",
              marginTop: 3,
            }}
          >
            {t("ersetzenWichtigstenOutlookTeams")}
          </div>
        </div>
        <span
          style={{
            fontFamily: T.sans,
            fontSize: 14.5,
            fontWeight: 600,
            color: T.ink,
            flex: "none",
          }}
        >
          {calc.msMailLabel} €
        </span>
      </div>

      <GroupDivider
        label={compact ? t("bleibtGleich") : t("bleibtGleichRuehrenNicht")}
        accent={false}
      />
      {rightUnchangedLabels(t).map((label) => (
        <div
          key={label}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "9px 0",
            borderTop: "1px solid rgba(11,15,25,0.06)",
          }}
        >
          <span
            style={{
              fontFamily: T.sans,
              fontSize: 13.5,
              fontWeight: 500,
              color: "#8A8D94",
            }}
          >
            {label}
          </span>
          <span
            style={{
              fontFamily: T.label,
              fontSize: 9,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#9AA0A6",
              flex: "none",
            }}
          >
            {t("unveraendert")}
          </span>
        </div>
      ))}
    </>
  );
}

export const COMPARE_CARD_STYLE = {
  borderTop: "1px solid rgba(11,15,25,0.06)",
} satisfies CSSProperties;
